// Organisation feature store. Values are strings, matching features.<name>.<key> on the org.
// Trial dates are Australia/Perth instants. The end instant is midnight on the day after
// the last day of the trial, so the date shown here is the last day itself.
//   features.trial.trialStartDate / trialEndDate
//   features.threeSixty.enabled / features.aiAssisted.enabled  ("true" or "false")

export type OrgFeatureMap = { [feature: string]: { [key: string]: string } | undefined };

const TZ = "Australia/Perth";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function featureValue(features: OrgFeatureMap | undefined, feature: string, key: string): string | undefined {
    const value = features?.[feature]?.[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** On when entitled, Off when explicitly disabled, Not set when the feature has never been stored. */
export function featureStatus(features: OrgFeatureMap | undefined, feature: string): string {
    const values = features?.[feature];
    if (!values) {
        return "Not set";
    }
    return values.enabled === "true" ? "On" : "Off";
}

export function formatTrialDate(ymd: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!match) {
        return ymd;
    }
    const month = MONTHS[Number(match[2]) - 1];
    if (!month) {
        return ymd;
    }
    return `${Number(match[3])} ${month} ${match[1]}`;
}

export function toStartDate(iso?: string): string {
    return perthYmd(iso);
}

export function toEndDate(iso?: string): string {
    if (!iso) {
        return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    return perthYmd(new Date(date.getTime() - 1000).toISOString());
}

function perthYmd(iso?: string): string {
    if (!iso) {
        return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    const year = get("year");
    const month = get("month");
    const day = get("day");
    if (!year || !month || !day) {
        return "";
    }
    return `${year}-${month}-${day}`;
}
